import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Label,
  Row,
} from "reactstrap";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { useTranslation } from "react-i18next";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { Trash2 } from "react-feather";
import * as Yup from "yup";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Breadcrumbs from "../../../common-elements/breadcrumbs/Breadcrumbs";
import { useEffect, useState } from "react";

// Interfaces
interface Benefit {
  id: string | number;
  description: string;
}

interface MemberPlan {
  id: number;
  type: string;
  subscriptionType: string;
  activationPrice: number;
  priceMonthly: number;
  priceYearly: number;
  benefit: Benefit[];
}

// Dummy Data
const becomeMemberData: MemberPlan[] = [
  {
    id: 1,
    type: "Linnk Hub",
    subscriptionType: "periodically",
    activationPrice: 150,
    priceMonthly: 50,
    priceYearly: 500,
    benefit: [
      { id: 101, description: "Access to basic features" },
      { id: 102, description: "5 projects limit" },
      { id: 103, description: "Email support" },
    ],
  },
  {
    id: 2,
    type: "Linnk Me",
    subscriptionType: "periodically",
    activationPrice: 150,
    priceMonthly: 50,
    priceYearly: 500,
    benefit: [
      { id: 201, description: "Access to all features" },
      { id: 202, description: "Unlimited projects" },
      { id: 203, description: "Priority email support" },
      { id: 204, description: "Monthly reports" },
    ],
  },
  {
    id: 3,
    type: "Linnk Up",
    subscriptionType: "periodically",
    activationPrice: 150,
    priceMonthly: 50,
    priceYearly: 500,
    benefit: [
      { id: 301, description: "All Pro features" },
      { id: 302, description: "Dedicated account manager" },
      { id: 303, description: "Custom integrations" },
      { id: 304, description: "Weekly reports" },
      { id: 305, description: "API access" },
    ],
  },
  {
    id: 4,
    type: "HRMS",
    subscriptionType: "onetime",
    activationPrice: 300,
    priceMonthly: 0,
    priceYearly: 0,
    benefit: [
      { id: 401, description: "All Pro features" },
      { id: 402, description: "Dedicated account manager" },
      { id: 403, description: "Custom integrations" },
      { id: 404, description: "Weekly reports" },
      { id: 405, description: "API access" },
    ],
  },
  {
    id: 5,
    type: "Finance/Accounts",
    subscriptionType: "periodically",
    activationPrice: 150,
    priceMonthly: 50,
    priceYearly: 500,
    benefit: [
      { id: 501, description: "All Pro features" },
      { id: 502, description: "Dedicated account manager" },
      { id: 503, description: "Custom integrations" },
      { id: 504, description: "Weekly reports" },
      { id: 505, description: "API access" },
    ],
  },
  {
    id: 6,
    type: "White Labeling",
    subscriptionType: "onetime",
    activationPrice: 300,
    priceMonthly: 0,
    priceYearly: 0,
    benefit: [
      { id: 601, description: "All Pro features" },
      { id: 602, description: "Dedicated account manager" },
      { id: 603, description: "Custom integrations" },
      { id: 604, description: "Weekly reports" },
      { id: 605, description: "API access" },
    ],
  },
  {
    id: 7,
    type: "App White Labeling",
    subscriptionType: "periodically",
    activationPrice: 150,
    priceMonthly: 50,
    priceYearly: 500,
    benefit: [
      { id: 701, description: "All Pro features" },
      { id: 702, description: "Dedicated account manager" },
      { id: 703, description: "Custom integrations" },
      { id: 704, description: "Weekly reports" },
      { id: 705, description: "API access" },
    ],
  },
];

const memberPlanValidationSchema = Yup.object({
  type: Yup.string().required("Plan type is required"),
  subscriptionType: Yup.string()
    .oneOf(["onetime", "periodically"])
    .required("Subscription type is required"),
  activationPrice: Yup.number()
    .typeError("Activation price must be a number")
    .min(0, "Activation price must be non-negative")
    .required("Activation price is required"),
  priceMonthly: Yup.number()
    .typeError("Monthly price must be a number")
    .min(0, "Monthly price must be non-negative")
    .when("subscriptionType", (subscriptionType, schema) => {
      if (subscriptionType && subscriptionType[0] === "periodically") {
        return schema.required("Monthly price is required");
      }
      return schema.notRequired();
    }),
  priceYearly: Yup.number()
    .typeError("Yearly price must be a number")
    .min(0, "Yearly price must be non-negative")
    .when("subscriptionType", (subscriptionType, schema) => {
      if (subscriptionType && subscriptionType[0] === "periodically") {
        return schema.required("Yearly price is required");
      }
      return schema.notRequired();
    }),
  benefits: Yup.array()
    .of(Yup.string().required("Benefit description is required"))
    .min(1, "At least one benefit is required"),
});

const AddAddons = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState({
    type: "",
    subscriptionType: "onetime", // Changed initial value
    activationPrice: "",
    priceMonthly: "",
    priceYearly: "",
    benefits: [] as string[],
    newBenefit: "",
  });

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      const plan = becomeMemberData.find((p) => p.id === parseInt(id));
      if (plan) {
        setInitialValues({
          type: plan.type,
          subscriptionType: plan.subscriptionType,
          activationPrice: plan.activationPrice.toString(),
          priceMonthly: plan.priceMonthly.toString(),
          priceYearly: plan.priceYearly.toString(),
          benefits: plan.benefit.map((b: Benefit) => b.description),
          newBenefit: "",
        });
      } else {
        navigate(`${process.env.PUBLIC_URL}/addons`);
      }
    } else {
      setIsEditMode(false);
      setInitialValues({
        type: "",
        subscriptionType: "onetime", // Changed initial value
        activationPrice: "",
        priceMonthly: "",
        priceYearly: "",
        benefits: [],
        newBenefit: "",
      });
    }
  }, [id, navigate]);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const handleSubmit = (values: typeof initialValues) => {
    alert(t("Save functionality is disabled as data is static."));
    navigate(`${process.env.PUBLIC_URL}/addons`);
  };

  const handleClickCancel = () => {
    navigate(`${process.env.PUBLIC_URL}/addons`);
  };

  return (
    <div className="page-body">
      <Breadcrumbs
        mainTitle={isEditMode ? t("Edit Add-On") : t("Add Add-On")}
        parents={[
          {
            label: t("Add-On"),
            link: `${process.env.PUBLIC_URL}/addons`,
          },
        ]}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <Formik
                  initialValues={initialValues}
                  validationSchema={memberPlanValidationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ setFieldValue, values }) => (
                    <Form className="theme-form">
                      <Row>
                        <Col>
                          <FormGroup>
                            <Label>
                              {t("Plan Name")}{" "}
                              <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              name="type"
                              type="text"
                              className="form-control"
                              placeholder={t("Enter Plan Name")}
                            />
                            <ErrorMessage
                              name="type"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>

                          <FormGroup>
                            <Label>
                              {t("Subscription Type")}{" "}
                              <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              as="select"
                              name="subscriptionType"
                              className="form-select"
                              onChange={(
                                e: React.ChangeEvent<HTMLSelectElement>
                              ) => {
                                const value = e.target.value;
                                setFieldValue("subscriptionType", value);
                                // Reset price fields when subscription type changes
                                setFieldValue("activationPrice", "");
                                setFieldValue("priceMonthly", "");
                                setFieldValue("priceYearly", "");
                              }}
                            >
                              <option value="onetime">{t("One Time")}</option>
                              <option value="periodically">
                                {t("Periodically")}
                              </option>
                            </Field>
                            <ErrorMessage
                              name="subscriptionType"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>

                          {values.subscriptionType && (
                            <FormGroup>
                              <Label>
                                {t("Activation Price")}{" "}
                                <span className="txt-danger">*</span>
                              </Label>
                              <Field
                                name="activationPrice"
                                type="number"
                                className="form-control"
                                placeholder={t("Enter Activation Price")}
                              />
                              <ErrorMessage
                                name="activationPrice"
                                component="span"
                                className="text-danger"
                              />
                            </FormGroup>
                          )}

                          {values.subscriptionType === "periodically" && (
                            <>
                              <FormGroup>
                                <Label>
                                  {t("Monthly Price")}{" "}
                                  <span className="txt-danger">*</span>
                                </Label>
                                <Field
                                  name="priceMonthly"
                                  type="number"
                                  className="form-control"
                                  placeholder={t("Enter Monthly Price")}
                                />
                                <ErrorMessage
                                  name="priceMonthly"
                                  component="span"
                                  className="text-danger"
                                />
                              </FormGroup>

                              <FormGroup>
                                <Label>
                                  {t("Yearly Price")}{" "}
                                  <span className="txt-danger">*</span>
                                </Label>
                                <Field
                                  name="priceYearly"
                                  type="number"
                                  className="form-control"
                                  placeholder={t("Enter Yearly Price")}
                                />
                                <ErrorMessage
                                  name="priceYearly"
                                  component="span"
                                  className="text-danger"
                                />
                              </FormGroup>
                            </>
                          )}

                          <FormGroup>
                            <Label>
                              {t("Benefits")}{" "}
                              <span className="txt-danger">*</span>
                            </Label>
                            <div className="input-group">
                              <Field
                                name="newBenefit"
                                type="text"
                                className="form-control"
                                placeholder={t("Enter Benefit Description")}
                              />
                              <Button
                                color="primary"
                                type="button"
                                onClick={() => {
                                  if (values.newBenefit.trim()) {
                                    setFieldValue("benefits", [
                                      ...values.benefits,
                                      values.newBenefit,
                                    ]);
                                    setFieldValue("newBenefit", "");
                                  }
                                }}
                              >
                                {t("Add Benefit")}
                              </Button>
                            </div>
                            <ErrorMessage
                              name="benefits"
                              component="span"
                              className="text-danger d-block mt-2"
                            />
                          </FormGroup>

                          <DragDropContext
                            onDragEnd={(result) => {
                              if (!result.destination) return;
                              const items = Array.from(values.benefits);
                              const [reorderedItem] = items.splice(
                                result.source.index,
                                1
                              );
                              items.splice(
                                result.destination.index,
                                0,
                                reorderedItem
                              );
                              setFieldValue("benefits", items);
                            }}
                          >
                            <Droppable
                              droppableId="benefits"
                              direction="vertical"
                            >
                              {(provided) => (
                                <div
                                  {...provided.droppableProps}
                                  ref={provided.innerRef}
                                  className="mt-3 d-flex flex-column gap-2 w-100"
                                  style={{ minHeight: "60px" }}
                                >
                                  {values.benefits.map((benefit, index) => (
                                    <Draggable
                                      key={`${benefit}-${index}`}
                                      draggableId={`${benefit}-${index}`}
                                      index={index}
                                    >
                                      {(provided) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          className="border border-1 border-gray rounded-2 d-flex align-items-center p-2 w-100" // Added w-100 and removed me-2
                                        >
                                          <span
                                            className="drag-handle d-flex align-items-center justify-content-center me-2"
                                            style={{
                                              cursor: "grab",
                                              fontSize: 18,
                                              color: "gray",
                                            }}
                                          >
                                            ⠿
                                          </span>
                                          <span className="me-2 d-flex align-items-center">
                                            {benefit}
                                          </span>
                                          <Button
                                            color="link"
                                            size="sm"
                                            className="ms-auto p-0 d-flex align-items-center"
                                            type="button"
                                            onClick={() => {
                                              const newBenefits =
                                                values.benefits.filter(
                                                  (_, i) => i !== index
                                                );
                                              setFieldValue(
                                                "benefits",
                                                newBenefits
                                              );
                                            }}
                                          >
                                            <Trash2 size={15} />
                                          </Button>
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>

                          <div className="text-end mt-3">
                            <Button
                              onClick={handleClickCancel}
                              color="secondary"
                              className="me-2"
                            >
                              {t("Cancel")}
                            </Button>
                            <Button color="primary" type="submit">
                              {t(isEditMode ? "Update" : "Save")}
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AddAddons;
